import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, GripVertical, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SectionsPage = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  useEffect(() => { loadSections(); }, []);

  const loadSections = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/sections`, { headers: getHeaders() });
      setSections(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const toggleSection = async (sectionId, currentEnabled) => {
    setToggling(sectionId);
    try {
      await axios.put(`${API}/api/admin/sections/${sectionId}`, { enabled: !currentEnabled }, { headers: getHeaders() });
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, enabled: !currentEnabled } : s));
    } catch (err) { console.error(err); }
    setToggling(null);
  };

  const enabledCount = sections.filter(s => s.enabled).length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="sections-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Sections Management</h1>
        <p className="text-sm text-slate-400 mt-1">{enabledCount} of {sections.length} sections active on your website</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-50 bg-slate-50/50 flex items-center justify-between">
          <h2 className="font-semibold text-slate-700">Website Sections</h2>
          <span className="text-xs text-slate-400">Toggle to show/hide sections on your website</span>
        </div>

        <div className="divide-y divide-slate-50">
          {sections.map((section, index) => (
            <div
              key={section.id}
              data-testid={`section-row-${section.id}`}
              className={`px-6 py-5 flex items-center gap-5 transition-colors ${!section.enabled ? 'bg-slate-50/50' : ''}`}
            >
              {/* Drag handle visual */}
              <div className="text-slate-200 flex-shrink-0">
                <GripVertical className="w-4 h-4" />
              </div>

              {/* Order number */}
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${section.enabled ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-300'}`}>
                {index + 1}
              </div>

              {/* Section info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-semibold transition-colors ${section.enabled ? 'text-slate-700' : 'text-slate-400'}`}>
                    {section.name_en}
                  </span>
                  <span className={`text-sm transition-colors ${section.enabled ? 'text-slate-400' : 'text-slate-300'}`}>
                    {section.name_ar}
                  </span>
                </div>
                <p className="text-xs text-slate-300 mt-0.5">
                  #{section.id}
                </p>
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${section.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                  {section.enabled ? 'Active' : 'Hidden'}
                </span>

                {/* Toggle button */}
                <button
                  data-testid={`toggle-section-${section.id}`}
                  onClick={() => toggleSection(section.id, section.enabled)}
                  disabled={toggling === section.id}
                  className={`relative w-12 h-7 rounded-full transition-all duration-300 flex-shrink-0 ${section.enabled ? 'bg-blue-500' : 'bg-slate-200'}`}
                >
                  {toggling === section.id ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                    </div>
                  ) : (
                    <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-all duration-300 ${section.enabled ? 'left-[22px]' : 'left-0.5'}`} />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="mt-6 p-4 bg-blue-50/50 rounded-xl border border-blue-100/50">
        <p className="text-sm text-blue-600/70">
          Changes take effect immediately on the website. Disabled sections will be completely hidden from visitors.
        </p>
      </div>
    </div>
  );
};

export default SectionsPage;
